import { Inject, Injectable, OnDestroy } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of, Subscription } from "rxjs";
import { catchError, distinctUntilChanged, map } from "rxjs/operators";

import { LaunchDarklyService } from "app/launchdarkly.service";
import en from "assets/i18n/en.json";

export interface AppLanguage {
  code: string;
  label: string;
}

export const DEFAULT_LANGUAGE = "en";

export const SUPPORTED_LANGUAGES: AppLanguage[] = [
  { code: "en", label: "English" },
  { code: "ro", label: "Română" }
];

export const LANGUAGE_SWITCHER_FLAG = "web-language-switcher";

const STORAGE_KEY = "language";

@Injectable({ providedIn: "root" })
export class LanguageService implements OnDestroy {
  readonly languages = SUPPORTED_LANGUAGES;

  private flagSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private ld: LaunchDarklyService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  get current(): string {
    return this.translate.currentLang || DEFAULT_LANGUAGE;
  }

  // English is bundled, so startup never waits on a network request.
  // A stored language is only applied while the switcher flag is on;
  // turning the flag off sends everyone back to English.
  init(): void {
    this.translate.addLangs(this.languages.map((l) => l.code));
    this.translate.setTranslation(DEFAULT_LANGUAGE, en);
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.apply(DEFAULT_LANGUAGE).subscribe();

    this.flagSub = this.ld.flags$
      .pipe(
        map((flags) => !!flags[LANGUAGE_SWITCHER_FLAG]),
        distinctUntilChanged()
      )
      .subscribe((enabled) => {
        const lang = enabled ? this.getStored() : null;
        this.apply(lang || DEFAULT_LANGUAGE).subscribe();
      });
  }

  use(code: string): Observable<unknown> {
    const lang = this.isSupported(code) ? code : DEFAULT_LANGUAGE;
    this.store(lang);
    return this.apply(lang);
  }

  ngOnDestroy(): void {
    this.flagSub?.unsubscribe();
  }

  // Falls back to English if the language file can't be loaded.
  private apply(code: string): Observable<unknown> {
    const lang = this.isSupported(code) ? code : DEFAULT_LANGUAGE;
    return this.translate.use(lang).pipe(
      map((res) => {
        this.document.documentElement.lang = lang;
        return res;
      }),
      catchError(() => {
        this.document.documentElement.lang = DEFAULT_LANGUAGE;
        return lang === DEFAULT_LANGUAGE
          ? of(null)
          : this.translate.use(DEFAULT_LANGUAGE);
      })
    );
  }

  private isSupported(code: string): boolean {
    return this.languages.some((l) => l.code === code);
  }

  private getStored(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private store(code: string): void {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {}
  }
}
